import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { ValidationResult } from "../../api/import_export";

interface ImportConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  file: File | null;
  validation: ValidationResult | null;
}

const ImportConfirmationDialog: React.FC<ImportConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  file,
  validation,
}) => {
  const handleClose = () => {
    onClose();
  };

  const canProceed =
    validation?.success && !validation?.validation_errors?.length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Confirm Import</DialogTitle>
      <DialogContent>
        {file && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              File: {file.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Size: {(file.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>
        )}

        {validation?.success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✓{" "}
            {validation.message ||
              `CSV validation successful! Found ${validation.total_rows?.toLocaleString()} valid phrases ready to import.`}
          </Alert>
        )}

        {validation?.validation_errors &&
          validation.validation_errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                CSV Validation Errors Found ({validation.validation_errors.length}):
              </Typography>
              <Box 
                sx={{ 
                  maxHeight: 300, 
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mt: 1,
                  mb: 1
                }}
              >
                <List dense>
                  {validation.validation_errors.map((error, index) => (
                    <ListItem key={index} sx={{ pl: 2 }}>
                      <ListItemText
                        primary={error.message}
                        secondary={`Rows: ${error.rows}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Please fix these errors in your CSV file before importing.
              </Typography>
            </Alert>
          )}

        {!validation?.success &&
          (validation?.error || validation?.message) &&
          !validation?.validation_errors?.length && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Import Error:
              </Typography>
              <Typography variant="body2">
                {validation.message || validation.error || "An unknown error occurred"}
              </Typography>
            </Alert>
          )}

        {canProceed && (
          <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>What happens next:</strong>
              <br />
              • Existing phrases (same text + language) will be updated
              <br />
              • New phrases will be created
              <br />• You'll see a progress summary when complete
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={!canProceed}
          color={canProceed ? "primary" : "error"}
        >
          {canProceed ? "Start Import" : "Fix Errors First"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportConfirmationDialog;
